// Network signal — banda + qualidade da conexão.
//
// Combina:
// 1. Network Information API (`navigator.connection`) — Chromium
//    Android/Tizen 5+/webOS 5+. Retorna effectiveType (4g/3g/2g/slow-2g)
//    + downlink (Mbps estimados pelo browser).
// 2. Bandwidth probe medida durante login (snapshot download) —
//    persistida em catalog-store. Caller injeta via `setLastMeasured`.
//
// Diferente dos outros signals (CPU/RAM/storage/viewport ESTÁVEIS),
// network é **dinâmico** — pode mudar durante a sessão. Capabilities
// que dependem disso devem subscribe via `subscribeNetwork`.

export type NetworkClass = 'unknown' | 'poor' | 'slow' | 'fast'

export interface NetworkSignal {
  class: NetworkClass
  /** Mbps estimados (Network Info API ou medição própria). null se desconhecido. */
  mbps: number | null
  /** Qual fonte: 'measured' (probe), 'navigator' (NetInfo), 'unknown'. */
  source: 'measured' | 'navigator' | 'unknown'
  /** SaveData do browser (data-saver mode). Reduz prefetch + quality. */
  saveData: boolean
}

interface NetInfo {
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g'
  downlink?: number
  saveData?: boolean
  addEventListener?(type: 'change', cb: () => void): void
  removeEventListener?(type: 'change', cb: () => void): void
}

let _measuredMbps: number | null = null

/** Caller (catalog-snapshot.ts) injeta após medir banda no login. */
export function setLastMeasuredMbps(mbps: number | null): void {
  _measuredMbps = mbps
  notifySubscribers()
}

function classify(mbps: number): NetworkClass {
  if (mbps < 1.5) return 'poor'
  if (mbps < 5) return 'slow'
  return 'fast'
}

export function readNetworkSignal(): NetworkSignal {
  const nav =
    typeof navigator !== 'undefined'
      ? (navigator as Navigator & { connection?: NetInfo })
      : null
  const conn = nav?.connection
  const saveData = conn?.saveData === true

  // Prioriza medição real (mais confiável que estimativa do browser)
  if (_measuredMbps !== null) {
    return {
      class: classify(_measuredMbps),
      mbps: _measuredMbps,
      source: 'measured',
      saveData,
    }
  }

  // Fallback: Network Info API
  if (conn?.downlink !== undefined && conn.downlink > 0) {
    return {
      class: classify(conn.downlink),
      mbps: conn.downlink,
      source: 'navigator',
      saveData,
    }
  }

  // Indisponível — caller decide (geralmente otimista 'fast')
  return { class: 'unknown', mbps: null, source: 'unknown', saveData }
}

// ===== Subscribe pra mudanças (rede oscila durante sessão) =====
const subscribers = new Set<() => void>()
let navListenerCleanup: (() => void) | null = null

function notifySubscribers(): void {
  // Itera cópia — defesa contra subscribers que se desinscrevem no callback.
  for (const cb of [...subscribers]) cb()
}

function attachNavListener(): void {
  if (navListenerCleanup) return
  if (typeof navigator === 'undefined') return
  const conn = (navigator as Navigator & { connection?: NetInfo }).connection
  if (!conn?.addEventListener || !conn.removeEventListener) return
  const handler = (): void => notifySubscribers()
  conn.addEventListener('change', handler)
  navListenerCleanup = () => conn.removeEventListener?.('change', handler)
}

function detachNavListenerIfIdle(): void {
  if (subscribers.size === 0 && navListenerCleanup) {
    navListenerCleanup()
    navListenerCleanup = null
  }
}

export function subscribeNetwork(cb: () => void): () => void {
  subscribers.add(cb)
  attachNavListener()
  return () => {
    subscribers.delete(cb)
    detachNavListenerIfIdle()
  }
}

/** Reset interno pra isolation em tests. NÃO usar em runtime. */
export function __resetNetworkSubscribersForTests(): void {
  subscribers.clear()
  if (navListenerCleanup) {
    navListenerCleanup()
    navListenerCleanup = null
  }
}
